import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [orgLoading, setOrgLoading] = useState(true)
  const [darkMode, setDarkModeState] = useState(() => {
    // Initialize from localStorage for instant load
    return localStorage?.getItem('wilcart_dark_mode') === 'true';
  });

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document?.documentElement?.classList?.add('dark');
    } else {
      document?.documentElement?.classList?.remove('dark');
    }
    localStorage?.setItem('wilcart_dark_mode', String(darkMode));
  }, [darkMode]);

  const toggleDarkMode = async () => {
    const newVal = !darkMode;
    setDarkModeState(newVal);
    // Persist to profile if logged in
    if (user) {
      await supabase?.from('profiles')?.update({ dark_mode: newVal })?.eq('id', user?.id);
    }
  };

  const loadProfileAndOrg = async (userId) => {
    if (!userId) {
      setOrgLoading(false)
      return
    }
    setOrgLoading(true)
    try {
      let { data: profileData, error: profileError } = await supabase?.from('profiles')?.select('*')?.eq('id', userId)?.single()

      // If no profile exists yet (e.g. email-invited user whose trigger hasn't fired),
      // create one from the auth user's metadata
      if (profileError || !profileData) {
        const { data: { user: authUser } } = await supabase?.auth?.getUser()
        if (authUser) {
          const meta = authUser?.user_metadata || {}
          const role = meta?.role === 'admin' ? 'admin' : 'member'
          let orgId = null
          try { orgId = meta?.org_id || null } catch {}

          const { data: newProfile, error: createError } = await supabase?.from('profiles')?.upsert({
            id: userId,
            email: authUser?.email || '',
            full_name: meta?.full_name || authUser?.email?.split('@')?.[0] || '',
            org_id: orgId,
            role: role,
            extended_role: ['technician', 'dispatcher']?.includes(meta?.role) ? meta?.role : null,
          }, { onConflict: 'id' })?.select()?.single()

          if (!createError && newProfile) {
            profileData = newProfile
          } else {
            setOrgLoading(false)
            return
          }
        } else {
          setOrgLoading(false)
          return
        }
      }

      setProfile(profileData)

      // Sync dark mode from profile
      if (profileData?.dark_mode !== undefined && profileData?.dark_mode !== null) {
        setDarkModeState(profileData?.dark_mode);
      }

      if (profileData?.org_id) {
        const { data: orgData } = await supabase?.from('organizations')?.select('*')?.eq('id', profileData?.org_id)?.single()
        if (orgData) setOrganization(orgData)
      }
    } catch (err) {
      console.error('Profile load error:', err)
    } finally {
      setOrgLoading(false)
    }
  }

  const clearState = () => {
    setUser(null)
    setProfile(null)
    setOrganization(null)
    setOrgLoading(false)
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        loadProfileAndOrg(session?.user?.id)
      } else {
        setOrgLoading(false)
      }
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        loadProfileAndOrg(session?.user?.id)
      } else {
        clearState()
      }
    })

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `https://dashboard.wilcart.com/auth/callback`,
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName, orgName, inviteToken = null) => {
    try {
      const metadata = inviteToken
        ? { full_name: fullName, invite_token: inviteToken }
        : { full_name: fullName, org_name: orgName }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: { data: metadata }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) clearState()
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase?.from('profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error && data) setProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateOrganization = async (updates) => {
    if (!organization) return { error: { message: 'No organization found' } }
    try {
      const { data, error } = await supabase?.from('organizations')?.update(updates)?.eq('id', organization?.id)?.select()?.single()
      if (!error && data) setOrganization(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Refresh organization data from DB
  const refreshOrganization = async () => {
    if (!profile?.org_id) return
    try {
      const { data: orgData } = await supabase?.from('organizations')?.select('*')?.eq('id', profile?.org_id)?.single()
      if (orgData) setOrganization(orgData)
    } catch (err) {
      console.error('Refresh org error:', err)
    }
  }

  const value = {
    user,
    profile,
    organization,
    loading,
    orgLoading,
    darkMode,
    toggleDarkMode,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    updateProfile,
    updateOrganization,
    refreshOrganization,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
